import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { ENTITY_TYPE_STORE, getEntityType } from './entityContext';

const COMPANY_API_BASE = (process.env.REACT_APP_API_COMPANY_URL || '').replace(/\/$/, '');

/** Industry names must match rows in industries_mt. */
export const HIRING_INDUSTRY_RESTAURANT = 'Restaurant';
export const HIRING_INDUSTRY_GROCERY_STORE = 'Grocery Store';

export function getHiringIndustryName(entityType = getEntityType()) {
  return entityType === ENTITY_TYPE_STORE
    ? HIRING_INDUSTRY_GROCERY_STORE
    : HIRING_INDUSTRY_RESTAURANT;
}

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function resolveResumeUrl(url, filePath) {
  const raw = url || filePath;
  if (!raw) return null;
  const text = String(raw).trim();
  if (!text) return null;

  if (text.startsWith('http://') || text.startsWith('https://')) {
    if (!COMPANY_API_BASE) return text;
    try {
      const parsed = new URL(text);
      const host = parsed.hostname;
      if (host === '127.0.0.1' || host === 'localhost') {
        return `${COMPANY_API_BASE}${parsed.pathname}${parsed.search}`;
      }
      return text;
    } catch {
      return text;
    }
  }

  const normalized = text.replace(/\\/g, '/').replace(/^\/+/, '');
  const relative = normalized.startsWith('customer/resume/')
    ? normalized.slice('customer/resume/'.length)
    : normalized;
  if (!COMPANY_API_BASE || !relative) return null;
  return `${COMPANY_API_BASE}/uploads/customer/resume/${relative}`;
}

export function normalizePreferredLocation(loc) {
  if (!loc || typeof loc !== 'object') return null;
  const anyAreaInCity = Boolean(loc.anyAreaInCity ?? loc.any_area_in_city);
  return {
    preferredLocationId: loc.preferredLocationId ?? loc.preferred_location_id ?? null,
    zipcode: loc.zipCode ?? loc.zip_code ?? loc.zipcode ?? '',
    area: loc.areaName ?? loc.area_name ?? loc.area ?? '',
    city: loc.cityName ?? loc.city_name ?? loc.city ?? '',
    anyAreaInCity,
  };
}

export function normalizeJobProfileToApplicant(profile) {
  if (!profile || typeof profile !== 'object') return null;
  const profileId =
    profile.customerProfileId ??
    profile.customer_profile_id ??
    profile.candidateId ??
    profile.candidate_id;
  if (profileId == null) return null;

  const preferredLocations = (profile.preferredLocations ?? profile.preferred_locations ?? [])
    .map(normalizePreferredLocation)
    .filter(Boolean);

  const name =
    profile.customerName ??
    profile.customer_name ??
    `Candidate #${profileId}`;

  const documents = profile.documents ?? [];
  const firstDoc = Array.isArray(documents) && documents.length > 0 ? documents[0] : null;
  const resumeFile =
    profile.resumeFile ??
    profile.resume_file ??
    profile.resumeDocumentName ??
    profile.resume_document_name ??
    firstDoc?.documentName ??
    firstDoc?.document_name ??
    null;
  const resumeUrl = resolveResumeUrl(
    profile.resumeUrl ??
      profile.resume_url ??
      firstDoc?.fileUrl ??
      firstDoc?.file_url,
    firstDoc?.filePath ?? firstDoc?.file_path,
  );

  return {
    id: profileId,
    name,
    role: profile.roleName ?? profile.role_name ?? '—',
    status: profile.status === 'ACTIVE' ? 'Open to work' : (profile.status ?? '—'),
    phone: profile.customerPhone ?? profile.customer_phone ?? '',
    email: profile.customerEmail ?? profile.customer_email ?? '',
    resumeFile,
    resumeUrl,
    experienceYears: profile.experienceYears ?? profile.experience_years ?? 0,
    preferredLocations,
    skills: profile.skills ?? '',
    currentCity: profile.currentCity ?? profile.current_city ?? '',
    employmentType: profile.employmentType ?? profile.employment_type ?? '',
    raw: profile,
  };
}

export async function fetchJobProfilesByIndustry(industry) {
  const industryName = String(industry ?? '').trim();
  if (!industryName) {
    throw new Error('industry is required');
  }

  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.jobProfiles, {
    headers: authHeaders(),
    params: { industry: industryName },
  });

  const data = response.data ?? {};
  const rows = data.jobProfiles ?? data.job_profiles ?? [];
  const list = Array.isArray(rows) ? rows : [];

  return {
    industryName: data.industryName ?? data.industry_name ?? industryName,
    count: data.count ?? list.length,
    applicants: list
      .map(normalizeJobProfileToApplicant)
      .filter(Boolean),
  };
}

export async function fetchJobProfilesForCurrentEntity() {
  return fetchJobProfilesByIndustry(getHiringIndustryName());
}

export async function fetchJobProfileByCandidateId(candidateId) {
  const id = Number(candidateId);
  if (!Number.isFinite(id)) {
    throw new Error('Invalid candidate id');
  }

  const data = await fetchJobProfilesForCurrentEntity();
  const found = data.applicants.find((applicant) => Number(applicant.id) === id);
  if (!found) {
    throw new Error('Candidate not found');
  }
  return found;
}
